import React from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@core/i18n';
import { SKYX_NEW_NS } from '@core/utils/skyxNewI18n';
import { useMenuOverlaySlideIn } from '@core/hooks/useMenuOverlaySlideIn';
import { chatRulesModalStyles as s } from '@core/lobby/skyxNewChatRulesModal.styles';
import { SkyxNewModalCloseButton } from '@core/UI/skyx/SkyxNewModalCloseButton';
import { SkyxNewModalPanelShell } from '@core/UI/skyx/SkyxNewModalPanelShell';

export interface SkyxNewChatRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SkyxNewChatRulesModal: React.FC<SkyxNewChatRulesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const t = useT(SKYX_NEW_NS);
  const { shouldRender, getOverlayStyle, getPanelStyle } =
    useMenuOverlaySlideIn(isOpen);

  if (!shouldRender) return null;

  const rules = t('ui.chat.rules.items', { returnObjects: true }) as string[];
  const safeRules = Array.isArray(rules) ? rules : [];

  const modal = (
    <div
      style={getOverlayStyle(s.overlay)}
      data-skyx-new-ui
      data-skyx-chat-rules-overlay
      onClick={onClose}
      role="presentation"
    >
      <SkyxNewModalPanelShell
        panelStyle={getPanelStyle(s.panel)}
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-labelledby="skyx-chat-rules-title"
        aria-modal="true"
      >
        <header style={s.header}>
          <h2 id="skyx-chat-rules-title" style={s.title}>
            {t('ui.chat.rules.title')}
          </h2>
          <SkyxNewModalCloseButton
            onClick={onClose}
            ariaLabel={t('ui.chat.rules.close')}
          />
        </header>

        <div style={s.body}>
          {safeRules.map((rule, index) => (
            <div key={index} style={s.ruleRow}>
              <span style={s.ruleBullet} aria-hidden>
                {'\u2022'}
              </span>
              <p style={s.ruleText}>{rule}</p>
            </div>
          ))}
        </div>
      </SkyxNewModalPanelShell>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modal, document.body);
  }
  return modal;
};
